import React, { forwardRef } from "react";

import ButtonUI from "./ButtonUI";
import renderIcon from "./renderIcon";

const cx = (...a) => a.filter(Boolean).join(" ");

/**
 * StatusUI.Spinner — rotița (dayview__spinner, ultra-view__spinner, .spinner din
 * LoadingOverlay, rotița de pe răspunsul trimis): cercul în culoarea textului, cu
 * pista la 20%, deci se vede pe ambele teme. Decorativă: textul de lângă ea spune
 * ce se întâmplă.
 *
 * size: sm (14px, pe un rând de text) | md (28px, implicit: calendarele) |
 *       lg (40px, o tură pe secundă: pornirea aplicației)
 * tone: accent (arcul în culoarea accentului, ca LoadingOverlay)
 */
function Spinner({ size = "md", tone, className = "", ...rest }) {
   return (
      <span
         className={cx(
            "statusUISpinner",
            size !== "md" && `statusUISpinner--${size}`,
            tone === "accent" && "statusUISpinner--accent",
            className,
         )}
         aria-hidden="true"
         {...rest}
      />
   );
}

/**
 * StatusUI — singura stare a aplicației: „se încarcă”, „nu există”, eroarea,
 * reușita, informarea, avertizarea. Înlocuiește StateCard, FormMessage,
 * StatusText, LoadingState + Spinner și candidații ErrorState, InlineAlert,
 * SlotOverlay, EmptyState cu iconiță, IssueList. Stilurile: scss/UI/_statusUI.scss.
 *
 * tone: neutral (implicit) | loading | empty | success | error | info | warning
 * look: text (implicit — textul simplu: neutral / loading / empty cursiv white-s,
 *          ca notificationsPopup__state; error / success / info / warning 13px în
 *          culoarea de text a tonului, ca messageTemplates__error / __ok) |
 *       card (cardul de stare 14×16, rază 18: neutral black-p / white-s, error
 *          red-t / red-d, success green-t / green-d, info blue-t / blue-d,
 *          warning orange-t / orange-d — studentsProfileUI__stateCard) |
 *       block (zona centrată cu înălțimea minimă 240: rotița la loading, iconița,
 *          titlul în culoarea tonului, textul, butonul dedesubt — dayview__loading,
 *          ErrorState, notificationsPopup__empty) |
 *       overlay (la fel, peste conținut: voal black-p 55% + blur 1px; părintele are
 *          position: relative — ultra-view__loading--overlay, cv2-loading, mesajul
 *          de peste ore)
 * title: titlul (la card, block, overlay) · children: mesajul; la loading, implicit
 *        „Se încarcă…” (null = fără text)
 * icon: nume UIIcon / svg importat / element; la loading (block, overlay) înlocuiește
 *       rotița implicită — de ex. <StatusUI.Spinner size="lg" tone="accent" />
 * action: dedesubt la block / overlay, în dreapta la card, după text la text
 *         (de ex. <ButtonUI>Reîncearcă</ButtonUI>)
 * onClose: × la capăt (banda INFO din uneltele de examen); closeLabel = numele lui
 * compact: block fără înălțimea minimă (16px în jur), pentru o listă sau un card
 * align: start (implicit) | center (textul centrat cu 20×12 în jur, ca today__empty)
 * as: elementul (implicit p la text, div în rest; "li" într-o listă)
 * Anunțul: loading = role „status” + aria-live polite; error la card / block /
 * overlay = role „alert”.
 */
const StatusUI = forwardRef(function StatusUI(
   {
      as,
      tone = "neutral",
      look = "text",
      title,
      icon,
      action,
      onClose,
      closeLabel = "Închide",
      compact = false,
      align = "start",
      className = "",
      children,
      ...rest
   },
   ref,
) {
   const isText = look === "text";
   const loading = tone === "loading";
   const Component = as || (isText ? "p" : "div");
   // în textul simplu (p, li) totul rămâne în linie
   const Part = isText ? "span" : "div";
   const message = children === undefined && loading ? "Se încarcă…" : children;
   const iconEl = icon
      ? renderIcon(icon, "statusUI__icon")
      : loading && (look === "block" || look === "overlay")
        ? <Spinner />
        : null;
   const role = loading ? "status" : tone === "error" && !isText ? "alert" : undefined;

   return (
      <Component
         ref={ref}
         role={role}
         aria-live={loading ? "polite" : undefined}
         className={cx(
            "statusUI",
            `statusUI--${look}`,
            tone !== "neutral" && `statusUI--${tone}`,
            compact && "statusUI--compact",
            align === "center" && "statusUI--center",
            className,
         )}
         {...rest}
      >
         {iconEl}
         {isText
            ? message
            : (title != null || message != null) && (
                 <div className="statusUI__body">
                    {title != null && <strong className="statusUI__title">{title}</strong>}
                    {message != null && <div className="statusUI__text">{message}</div>}
                 </div>
              )}
         {action && <Part className="statusUI__action">{action}</Part>}
         {onClose && (
            <ButtonUI
               variant="ghost"
               size="sm"
               icon="close"
               aria-label={closeLabel}
               title={closeLabel}
               onClick={onClose}
               className="statusUI__close"
            />
         )}
      </Component>
   );
});

StatusUI.Spinner = Spinner;

export default StatusUI;
